import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { useTranslation } from '@gem/modules/i18n';

import { IGroupFormInitialValues } from 'APP/main/PopupManager/components/GroupForm/GroupForm.types';
import { GroupType } from 'APP/model/group/groupModel.types';
import type { TValidateHandler } from 'APP/packages/validations';
import { useChatGroupValidations } from 'APP/packages/validations/forms/forms';

export interface IGroupFormTypeRadioOption {
  value: GroupType.Open | GroupType.Closed;
  label: string;
  description: string;
}

export interface IGroupFormGeneralPresenterParams {
  initialValues: IGroupFormInitialValues;
  isEditMode: boolean;
}

export interface IGroupFormGeneralPresenter {
  initialValues: IGroupFormInitialValues;
  isEditMode: boolean;
  radioOptions: IGroupFormTypeRadioOption[];
  pristine: boolean;
  nameValidate: TValidateHandler;
  aliasValidate: TValidateHandler;
  setIsEditMode(mode: boolean): void;
}

export const useGroupFormGeneralPresenter = (
  data: IGroupFormGeneralPresenterParams
): IGroupFormGeneralPresenter => {
  const { initialValues, isEditMode } = data;
  const { t } = useTranslation();
  const formValidations = useChatGroupValidations();

  const presenter = useLocalObservable<IGroupFormGeneralPresenter>(() => ({
    initialValues,
    isEditMode: isEditMode,
    radioOptions: [
      {
        value: GroupType.Closed,
        label: t('channel_private_option'),
        description: t('channel_private_option_subhint'),
      },
      {
        value: GroupType.Open,
        label: t('group_creation_open_type'),
        description: t('channel_public_option_subhint'),
      },
    ],

    get pristine(): boolean {
      return (
        presenter.isEditMode ||
        Object.values(presenter.initialValues).filter((value) => Boolean(value)).length <= 1
      );
    },

    get nameValidate(): TValidateHandler {
      return formValidations.groupName.validate;
    },

    get aliasValidate(): TValidateHandler {
      if (presenter.isEditMode) {
        return formValidations.groupAliasEdit.validate;
      }

      return formValidations.groupAliasCreate.validate;
    },

    setIsEditMode(mode: boolean): void {
      presenter.isEditMode = mode;
    },
  }));

  useEffect(() => {
    presenter.setIsEditMode(isEditMode);
  }, [isEditMode]);

  return presenter;
};
